package com.tregz.tremenz.io.model

import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch

/** Whether a fetch is already in flight, so a second call does not start another. */
private var isLoadingSongs = false
private var isLoadingChannels = false
private var isLoadingMixes = false

/** Logs what a parser dropped, and fails the load when it dropped everything it was given. */
private fun skipped(asset: String): ModelSkipped = { skipped, total ->
    if (skipped > 0) console.warn("$asset: dropped $skipped of $total entries")
    check(total == 0 || skipped < total) { "$asset: every entry was dropped" }
}

/** The body of a successful response; a 404 page or a gateway error is a failure, not a payload. */
private suspend fun fetchText(asset: String): String {
    val response = window.fetch(asset, js("{}")).await()
    check(response.ok) { "$asset: HTTP ${response.status}" }
    return response.text().await()
}

fun ModelRepository.loadSongs() {
    registerRetry("songs") { loadSongs() }
    if (SONGS.isNotEmpty()) {
        publishSongs()
    } else if (!isLoadingSongs) {
        isLoadingSongs = true
        reportSongs(ModelStatus.LOADING)
        MainScope().launch {
            try {
                setSongs(parseSongsJson(fetchText("miksing-export.json"), skipped("miksing-export.json")))
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                console.error("Error loading miksing-export.json: ", e)
                reportSongs(ModelStatus.FAILED)
            } finally {
                isLoadingSongs = false
            }
        }
    }
    loadChannels()
}

/**
 * Fetches `music_videos.json` — the Channels programming — once per page load. The Channels tab
 * observes [ModelRepository.CHANNELS_LIVE] and lights up once it lands.
 */
fun ModelRepository.loadChannels() {
    registerRetry("channels") { loadChannels() }
    if (CHANNELS_DATA.isNotEmpty() || isLoadingChannels) return
    isLoadingChannels = true
    reportChannels(ModelStatus.LOADING)
    MainScope().launch {
        try {
            setChannels(parseMusicVideosJson(fetchText("music_videos.json"), skipped("music_videos.json")))
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Error loading music_videos.json: ", e)
            reportChannels(ModelStatus.FAILED)
        } finally {
            isLoadingChannels = false
        }
    }
}

/**
 * Fetches `mixes.json` — the bundled mixes catalogue — for instant display on page load.
 */
fun ModelRepository.loadMixes() {
    registerRetry("mixes") { loadMixes() }
    if (ITEMS.any { it is ModelData.Media } || isLoadingMixes) return
    isLoadingMixes = true
    reportMixes(ModelStatus.LOADING)
    MainScope().launch {
        try {
            setMixes(parseMixesJson(fetchText("mixes.json"), skipped("mixes.json")))
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Error loading mixes.json: ", e)
            reportMixes(ModelStatus.FAILED)
        } finally {
            isLoadingMixes = false
        }
    }
}
